import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { createOrder } from "../api/orders";
import { euro } from "../api/shop";
import { useCart } from "../context/CartContext";
import { useAuth } from "../context/AuthContext";

const EMPTY = { idnumber: "", street: "", city: "", state: "", zip: "", phone: "" };

const LABELS = {
  idnumber: "DNI / NIE",
  street: "Dirección de entrega",
  city: "Ciudad",
  state: "Provincia",
  zip: "Código postal",
  phone: "Teléfono de contacto",
};

export default function Checkout() {
  const { items, subtotal, count, toOrderPayload, clear } = useCart();
  const { token } = useAuth();
  const navigate = useNavigate();

  const [address, setAddress] = useState(EMPTY);
  const [errors, setErrors] = useState({});
  const [status, setStatus] = useState("idle"); // idle | sending | done
  const [failure, setFailure] = useState("");

  if (status !== "done" && items.length === 0) {
    return (
      <div className="wrap" style={{ padding: "60px 0 90px" }}>
        <div className="state">
          <h3>No hay nada que pagar</h3>
          <p>Tu carrito está vacío.</p>
          <Link to="/moviles" className="btn btn--lg">
            Ver móviles
          </Link>
        </div>
      </div>
    );
  }

  if (status === "done") {
    return (
      <div className="wrap" style={{ padding: "60px 0 90px" }}>
        <div className="state">
          <p className="eyebrow">Pedido recibido</p>
          <h3 style={{ fontSize: "1.8rem", margin: "10px 0" }}>¡Gracias! Ya lo tenemos.</h3>
          <p>
            Te hemos enviado un correo con el detalle del pedido. Lo preparamos y sale en 24–72 h.
          </p>
          <Link to="/moviles" className="btn btn--lg">
            Seguir comprando
          </Link>
        </div>
      </div>
    );
  }

  const change = (e) => {
    const { name, value } = e.target;
    setAddress((current) => ({ ...current, [name]: value }));
    setErrors((current) => ({ ...current, [name]: "" }));
  };

  const validate = () => {
    const found = {};

    Object.keys(LABELS).forEach((key) => {
      if (!address[key].trim()) found[key] = "Este campo es obligatorio.";
    });

    if (address.zip && !/^\d{5}$/.test(address.zip.trim())) {
      found.zip = "El código postal tiene 5 dígitos.";
    }
    if (address.phone && !/^[\d\s+]{9,15}$/.test(address.phone.trim())) {
      found.phone = "Escribe un teléfono válido.";
    }

    setErrors(found);
    return Object.keys(found).length === 0;
  };

  const submit = async (e) => {
    e.preventDefault();
    setFailure("");
    if (!validate()) return;

    setStatus("sending");
    try {
      await createOrder({
        cart: toOrderPayload(),
        address,
        token,
      });
      clear();
      setStatus("done");
      window.scrollTo({ top: 0 });
    } catch (err) {
      setFailure(err.message);
      setStatus("idle");
    }
  };

  return (
    <>
      <div className="wrap pagehead">
        <p className="eyebrow">Paso 2 de 2</p>
        <h1 style={{ fontSize: "clamp(1.9rem, 4vw, 2.8rem)" }}>Datos de envío</h1>
      </div>

      <div className="wrap twocol">
        <form onSubmit={submit} noValidate>
          {failure && <div className="alert alert--error">{failure}</div>}

          <div className="formcard">
            <h3 style={{ marginBottom: 18 }}>¿Dónde te lo llevamos?</h3>

            <label className="field">
              <span>{LABELS.idnumber}</span>
              <input name="idnumber" value={address.idnumber} onChange={change} autoComplete="off" />
              {errors.idnumber && <small>{errors.idnumber}</small>}
            </label>

            <label className="field">
              <span>{LABELS.street}</span>
              <input
                name="street"
                value={address.street}
                onChange={change}
                autoComplete="street-address"
                placeholder="Calle, número, piso"
              />
              {errors.street && <small>{errors.street}</small>}
            </label>

            <div className="fieldrow">
              <label className="field">
                <span>{LABELS.city}</span>
                <input name="city" value={address.city} onChange={change} autoComplete="address-level2" />
                {errors.city && <small>{errors.city}</small>}
              </label>

              <label className="field">
                <span>{LABELS.state}</span>
                <input name="state" value={address.state} onChange={change} autoComplete="address-level1" />
                {errors.state && <small>{errors.state}</small>}
              </label>
            </div>

            <div className="fieldrow">
              <label className="field">
                <span>{LABELS.zip}</span>
                <input
                  name="zip"
                  value={address.zip}
                  onChange={change}
                  inputMode="numeric"
                  autoComplete="postal-code"
                />
                {errors.zip && <small>{errors.zip}</small>}
              </label>

              <label className="field">
                <span>{LABELS.phone}</span>
                <input name="phone" value={address.phone} onChange={change} autoComplete="tel" />
                {errors.phone && <small>{errors.phone}</small>}
              </label>
            </div>
          </div>

          <div className="formcard">
            <h3 style={{ marginBottom: 10 }}>Pago</h3>
            <p style={{ fontSize: "0.92rem", color: "var(--stone)" }}>
              Confirmas el pedido ahora y te contactamos para completar el pago. Recibirás un correo
              con todo el detalle.
            </p>
          </div>

          <button type="submit" className="btn btn--lg btn--block" style={{ marginTop: 20 }} disabled={status === "sending"}>
            {status === "sending" ? "Enviando…" : `Confirmar pedido · ${euro(subtotal)} €`}
          </button>

          <button type="button" className="linkish" style={{ marginTop: 16 }} onClick={() => navigate("/carrito")}>
            Volver al carrito
          </button>
        </form>

        <aside className="summary">
          <h3>Tu pedido</h3>

          {items.map((item) => (
            <div className="miniline" key={item.sku}>
              <span>
                {item.quantity} × {item.brand} {item.name}
              </span>
              <span>{euro(item.price * item.quantity)} €</span>
            </div>
          ))}

          <div className="summary__row" style={{ marginTop: 12, borderTop: "1px solid var(--line)" }}>
            <span>
              Subtotal ({count} {count === 1 ? "artículo" : "artículos"})
            </span>
            <span>{euro(subtotal)} €</span>
          </div>

          <div className="summary__row">
            <span>Envío</span>
            <span>Gratis</span>
          </div>

          <div className="summary__total">
            <span>Total</span>
            <b>{euro(subtotal)} €</b>
          </div>

          <p style={{ fontSize: "0.8rem", color: "var(--stone)", marginTop: 10 }}>
            IVA y canon digital incluidos.
          </p>
        </aside>
      </div>
    </>
  );
}
